import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUp, Paperclip } from 'lucide-react';
import { TooltipIconButton } from '../ui/tooltip-icon-button';
import { cn } from '../../lib/utils';
import ChatSourceSelect, { SourceType } from './ChatSourceSelect';
import ModeIndicator from './ModeIndicator';

interface ChatComposerToolbarProps {
  sourceType: SourceType;
  onSourceChange: (value: SourceType) => void;
  onAttach: () => void;
  onSend: () => void;
  /** False for an empty composer — the send button stays inert. */
  canSend: boolean;
  /** True while a reply streams; every control is inert. */
  disabled: boolean;
  /** Persists a mode switch against this thread, when there is one. */
  threadId?: string;
}

/** The row under the composer: attach, source, mode, send. */
const ChatComposerToolbar: React.FC<ChatComposerToolbarProps> = ({
  sourceType,
  onSourceChange,
  onAttach,
  onSend,
  canSend,
  disabled,
  threadId,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 px-0.5">
      <div className="flex items-center gap-1.5">
        <TooltipIconButton
          size="icon-sm"
          label={t('chat.attachFile')}
          icon={<Paperclip strokeWidth={1.8} />}
          onClick={onAttach}
          disabled={disabled}
        />

        <ChatSourceSelect value={sourceType} onChange={onSourceChange} disabled={disabled} />

        <ModeIndicator threadId={threadId} />
      </div>

      <TooltipIconButton
        size="icon-sm"
        variant="default"
        // Not top: that lands the tooltip on the composer's own Enter hint.
        side="left"
        label={t('chat.sendMessage')}
        icon={<ArrowUp strokeWidth={2.6} />}
        onClick={onSend}
        disabled={disabled || !canSend}
        className={cn(
          'rounded-full shadow-sm transition-[scale,box-shadow,background-color]',
          'hover:shadow-md active:scale-[0.96] motion-reduce:active:scale-100',
          // Disabled reads as "nothing to send" rather than "broken": it keeps
          // the shape but drops to a flat muted surface instead of a faded fill.
          'disabled:bg-muted disabled:text-muted-foreground disabled:opacity-100 disabled:shadow-none',
        )}
      />
    </div>
  );
};

export default ChatComposerToolbar;
